import React, { useCallback, useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import JourneyEditor from "../components/JourneyEditor";
import InviteStudentsCard from "../components/InviteStudentsCard";
import PlansEditor from "../components/PlansEditor";
import AccessControls from "../components/AccessControls";
import MentorsManagementTab from "../components/MentorsManagementTab";
import { PushComposer } from "../components/PushComposer";
import {
  ShieldAlert,
  Users,
  Search,
  AlertTriangle,
  Flame,
  DollarSign,
  TrendingUp,
  Activity,
  X,
  ChevronRight,
  Filter,
  Map,
  DollarSign as DollarIcon,
  Crown,
  Bell,
} from "lucide-react";

export default function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("students");
  const [students, setStudents] = useState([]);
  const [mentorships, setMentorships] = useState([]);
  const [selectedMentorship, setSelectedMentorship] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mentorMe, setMentorMe] = useState(null);
  const [studentView, setStudentView] = useState("active"); // active | archived

  const isOwner = user?.role === "platform_owner";

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/admin/mentorships");
        setMentorships(res.data.mentorships || []);
      } catch (err) {
        console.error("mentorships error:", err);
      }
      try {
        const meRes = await api.get("/api/mentor/me");
        setMentorMe(meRes.data);
      } catch (err) {
        // mentor/me may not be available for very old accounts — ignore
      }
    })();
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (stageFilter) params.stage_filter = stageFilter;
      if (selectedMentorship) params.mentorship_id = selectedMentorship;
      if (studentView === "archived") params.only_archived = true;
      const res = await api.get("/api/admin/students", { params });
      setStudents(res.data.students || []);
    } catch (err) {
      console.error("Admin error:", err);
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter, selectedMentorship, studentView]);

  const handleRemoveStudent = async (studentId, mentorshipId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remover este aluno da mentoria? O histórico será preservado, mas o acesso será revogado.")) return;
    try {
      const params = mentorshipId ? { mentorship_id: mentorshipId } : {};
      await api.post(`/api/admin/students/${studentId}/remove`, null, { params });
      await fetchStudents();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao remover aluno.");
    }
  };

  const handleRestoreStudent = async (studentId, mentorshipId) => {
    try {
      const params = mentorshipId ? { mentorship_id: mentorshipId } : {};
      await api.post(`/api/admin/students/${studentId}/restore`, null, { params });
      await fetchStudents();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao restaurar aluno.");
    }
  };

  useEffect(() => {
    if (activeTab === "students") {
      fetchStudents();
    }
  }, [fetchStudents, activeTab]);

  const handleInspectStudent = async (studentId) => {
    setSelectedStudentId(studentId);
    setDetailLoading(true);
    try {
      const res = await api.get(`/api/admin/students/${studentId}`);
      setStudentDetail(res.data);
    } catch (err) {
      alert("Erro ao carregar detalhes do aluno.");
    } finally {
      setDetailLoading(false);
    }
  };

  const fmtBRL = (val) =>
    `R$ ${Number(val || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getHealthBadge = (score) => {
    if (score <= 20) return "bg-red-500/20 text-red-400 border-red-500/30";
    if (score <= 40) return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    if (score <= 60) return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  };

  return (
    <div data-testid="admin-page-container" className="space-y-6 max-w-6xl mx-auto px-4 py-4 pb-20">
      {/* Header */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider mb-1">
            <ShieldAlert className="w-4 h-4" />
            <span>Painel do Mentor</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-white">
            Monitoramento de Operações dos Alunos
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Identifique rapidamente quem está com a saúde crítica e precisa de intervenção metodológica.
          </p>
        </div>

        <div className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#1E2330] border border-[#282D3D] text-gray-300 self-start sm:self-auto">
          {students.length} alunos monitorados
        </div>
      </div>

      {/* Pending mentor gate — block access for non-approved mentors */}
      {!isOwner && mentorMe && mentorMe.mentor_status && mentorMe.mentor_status !== "active" && (
        <div
          data-testid="mentor-pending-banner"
          className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-5 text-center space-y-3"
        >
          <div className="text-yellow-300 font-extrabold text-sm">
            {mentorMe.mentor_status === "pending" && "Sua conta de mentor está aguardando aprovação."}
            {mentorMe.mentor_status === "blocked" && "Sua conta de mentor está bloqueada."}
            {mentorMe.mentor_status === "rejected" && "Sua solicitação de mentor foi recusada."}
            {mentorMe.mentor_status === "archived" && "Sua conta de mentor foi removida pelo Dono da plataforma."}
          </div>
          <p className="text-xs text-yellow-100/80 leading-relaxed max-w-lg mx-auto">
            Escolha um plano no seu perfil e envie sua solicitação via WhatsApp para o Dono da plataforma.
            Assim que aprovado, você terá acesso completo ao painel de mentor.
          </p>
          <a
            href="/perfil"
            data-testid="mentor-pending-cta"
            className="inline-block px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
          >
            Ver planos e ativar conta
          </a>
        </div>
      )}

      {/* Tabs — hidden when mentor is not active */}
      {(isOwner || !mentorMe?.mentor_status || mentorMe?.mentor_status === "active") && (
      <div className="flex items-center gap-1 border-b border-[#282D3D] overflow-x-auto">
        <button
          data-testid="admin-tab-students"
          onClick={() => setActiveTab("students")}
          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
            activeTab === "students"
              ? "border-[#FF6B00] text-[#FF6B00]"
              : "border-transparent text-gray-400 hover:text-gray-200"
          }`}
        >
          <Users className="w-3.5 h-3.5" /> Alunos
        </button>
        <button
          data-testid="admin-tab-journey"
          onClick={() => setActiveTab("journey")}
          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
            activeTab === "journey"
              ? "border-[#FF6B00] text-[#FF6B00]"
              : "border-transparent text-gray-400 hover:text-gray-200"
          }`}
        >
          <Map className="w-3.5 h-3.5" /> Jornada
        </button>
        {isOwner && (
          <button
            data-testid="admin-tab-mentors"
            onClick={() => setActiveTab("mentors")}
            className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === "mentors"
                ? "border-[#FF6B00] text-[#FF6B00]"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <Crown className="w-3.5 h-3.5" /> Mentores
          </button>
        )}
        <button
          data-testid="admin-tab-plans"
          onClick={() => setActiveTab("plans")}
          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
            activeTab === "plans"
              ? "border-[#FF6B00] text-[#FF6B00]"
              : "border-transparent text-gray-400 hover:text-gray-200"
          }`}
        >
          <DollarIcon className="w-3.5 h-3.5" /> Planos
        </button>
        <button
          data-testid="admin-tab-push"
          onClick={() => setActiveTab("push")}
          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
            activeTab === "push"
              ? "border-[#FF6B00] text-[#FF6B00]"
              : "border-transparent text-gray-400 hover:text-gray-200"
          }`}
        >
          <Bell className="w-3.5 h-3.5" /> Notificações
        </button>
      </div>
      )}

      {(isOwner || !mentorMe?.mentor_status || mentorMe?.mentor_status === "active") && (
      <>
      {activeTab === "journey" && <JourneyEditor mentorships={mentorships} selectedMentorship={selectedMentorship} onSelectMentorship={setSelectedMentorship} />}
      {activeTab === "plans" && <PlansEditor />}
      {activeTab === "push" && <PushComposer mentorships={mentorships} isOwner={isOwner} />}
      {activeTab === "mentors" && isOwner && <MentorsManagementTab />}
      </>
      )}

      {activeTab === "students" && (isOwner || !mentorMe?.mentor_status || mentorMe?.mentor_status === "active") && (
      <>
      {mentorships.length > 1 && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-bold text-gray-400">Mentoria:</label>
          <select
            data-testid="mentorship-selector"
            value={selectedMentorship}
            onChange={(e) => setSelectedMentorship(e.target.value)}
            className="bg-[#14161C] border border-[#282D3D] rounded-lg py-1.5 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
          >
            <option value="">Todas ({mentorships.length})</option>
            {mentorships.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      )}
      <InviteStudentsCard mentorshipId={selectedMentorship} />
      {/* Ativos / Removidos toggle */}
      <div className="flex items-center gap-2">
        <button
          data-testid="students-filter-active"
          onClick={() => setStudentView("active")}
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border ${
            studentView === "active"
              ? "bg-[#FF6B00] text-black border-[#FF6B00]"
              : "bg-[#14161C] text-gray-300 border-[#282D3D]"
          }`}
        >
          Ativos
        </button>
        <button
          data-testid="students-filter-archived"
          onClick={() => setStudentView("archived")}
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border ${
            studentView === "archived"
              ? "bg-[#FF6B00] text-black border-[#FF6B00]"
              : "bg-[#14161C] text-gray-300 border-[#282D3D]"
          }`}
        >
          Removidos
        </button>
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Buscar aluno por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#14161C] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
          />
        </div>

        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="bg-[#14161C] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
        >
          <option value="">Todas as Fases</option>
          <option value="CAVE_PROFILE">Caverna — Perfil</option>
          <option value="CAVE_WHATSAPP">Caverna — WhatsApp</option>
          <option value="LIVE_PRETEST">Live — Pré-Teste</option>
          <option value="LIVE_TEST">Live — Teste</option>
          <option value="LIVE_PRESCALE">Live — Pré-Escala</option>
          <option value="LIVE_SCALE">Live — Escala</option>
        </select>
      </div>

      {/* Students Table / Cards (Sorted by Critical Health) */}
      <div className="space-y-3">
        {students.map((st) => (
          <div
            key={st.id}
            data-testid={`admin-student-row-${st.id}`}
            onClick={() => handleInspectStudent(st.id)}
            className={`bg-[#14161C] border rounded-xl p-4 transition-all hover:border-[#FF6B00] cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-3 ${
              st.is_critical ? "border-red-500/40 bg-red-950/10" : "border-[#282D3D]"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 border ${getHealthBadge(
                  st.health_score
                )}`}
              >
                {st.health_score}%
              </div>

              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-white">{st.name}</h3>
                  {st.is_critical && (
                    <span className="text-[10px] bg-red-600 text-white font-extrabold px-1.5 py-0.2 rounded">
                      🚨 CRÍTICO
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {st.email} • Fase: <strong className="text-gray-200">{st.stage}</strong>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <AccessControls student={st} onRefresh={fetchStudents} />
                  {studentView === "active" ? (
                    <button
                      data-testid={`remove-student-${st.id}`}
                      onClick={() => handleRemoveStudent(st.id, st.mentorship_id)}
                      className="text-[10px] font-bold px-2 py-1 rounded bg-red-600/20 text-red-300 border border-red-600/40 hover:bg-red-600/30"
                    >
                      Remover aluno
                    </button>
                  ) : (
                    <button
                      data-testid={`restore-student-${st.id}`}
                      onClick={() => handleRestoreStudent(st.id, st.mentorship_id)}
                      className="text-[10px] font-bold px-2 py-1 rounded bg-emerald-600/20 text-emerald-300 border border-emerald-600/40 hover:bg-emerald-600/30"
                    >
                      Restaurar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Quick indicators */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-[10px] text-gray-400 block">Receita / Gasto</span>
                <span className="font-bold text-white">
                  {fmtBRL(st.total_revenue)} / {fmtBRL(st.total_spend)}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block">ROI</span>
                <span className="font-extrabold text-[#FF6B00]">{st.roi}x</span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block">Última Venda</span>
                <span className="text-gray-300">
                  {st.hours_since_last_sale !== null ? `há ${st.hours_since_last_sale}h` : "Sem vendas"}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block">Assinatura</span>
                <span className="text-gray-300 font-mono capitalize">{st.subscription_status}</span>
              </div>
            </div>

            <ChevronRight className="w-4 h-4 text-gray-400 hidden md:block" />
          </div>
        ))}
      </div>
      </>
      )}

      {/* Inspect Student Drawer / Modal */}
      {selectedStudentId && studentDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative text-left shadow-2xl">
            <button
              onClick={() => {
                setSelectedStudentId(null);
                setStudentDetail(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FF6B00] text-black font-extrabold flex items-center justify-center text-lg">
                {studentDetail.student?.name?.charAt(0)}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{studentDetail.student?.name}</h2>
                <div className="text-xs text-gray-400">{studentDetail.student?.email}</div>
              </div>
            </div>

            <div className="space-y-4 text-xs">
              <div className="bg-[#1A1D26] p-3 rounded-xl border border-[#282D3D] space-y-2">
                <div className="font-bold text-white text-sm">Resumo da Operação</div>
                <div className="grid grid-cols-2 gap-2 text-gray-300">
                  <div>Fase Atual: <strong>{studentDetail.operation?.stage}</strong></div>
                  <div>Saúde: <strong>{studentDetail.operation?.health_score}%</strong></div>
                  <div>Receita Total: <strong>{fmtBRL(studentDetail.operation?.total_revenue)}</strong></div>
                  <div>Gasto Total: <strong>{fmtBRL(studentDetail.operation?.total_spend)}</strong></div>
                  <div>Vendas Concluídas: <strong>{studentDetail.operation?.total_sales_count}</strong></div>
                  <div>Leads Recebidos: <strong>{studentDetail.operation?.total_leads_count}</strong></div>
                </div>
              </div>

              {/* Next Action & Mentoring Tip */}
              <div className="bg-[#1E2230] p-3 rounded-xl border border-[#FF6B00]/30">
                <span className="font-bold text-[#FF6B00] block mb-1">🎯 Próxima Ação Recomendada:</span>
                <p className="text-white text-xs">{studentDetail.operation?.next_best_action}</p>
              </div>

              {/* Campaigns */}
              <div>
                <h4 className="font-bold text-white mb-2">Campanhas Registradas ({studentDetail.campaigns?.length || 0})</h4>
                <div className="space-y-2">
                  {studentDetail.campaigns?.map((cp) => (
                    <div key={cp.id || cp._id} className="p-2.5 bg-[#181B24] rounded-lg border border-[#282D3D] flex justify-between">
                      <div>
                        <span className="font-bold text-white block">{cp.name}</span>
                        <span className="text-gray-400">Orçamento: {fmtBRL(cp.daily_budget)}/dia</span>
                      </div>
                      <span className="text-[#FF6B00] font-mono font-bold">{cp.stage}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline History */}
              <div>
                <h4 className="font-bold text-white mb-2">Últimos Eventos Operacionais</h4>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {studentDetail.timeline?.map((evt, idx) => (
                    <div key={idx} className="p-2 bg-[#181B24] rounded border border-[#282D3D] flex justify-between">
                      <span className="text-gray-200">{evt.title}</span>
                      <span className="text-gray-400 font-mono text-[10px]">{evt.created_at?.slice(11, 16)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
